import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../store';
import { refreshDashboardTiles, selectActiveTileScope } from '../../store/entities/dashboardTiles';
import { selectIdentityCompanyId } from '../../store/entities/identity';
import { DashboardChartType } from '@api/models/enums/DashboardChartType';
import { DashboardPage } from '@api/models/enums/DashboardPage';
import { TileScope } from '@api/models/dashboard/TileScope';
import { ColumnMetaDto } from '@api/models/analytics/ColumnMetaDto';
import { dashboardTileService } from '@api/services/services';
import { ChartKind } from './components';
import { TileDialogConfig } from './components/TileDialog';
import { AnalyticsDomainConfig, NO_GROUP_BY } from './analyticsDomainConfig';

const chartKindToChartType = (kind: ChartKind): DashboardChartType => {
  switch (kind) {
    case 'pie':
      return DashboardChartType.Pie;
    case 'bar':
      return DashboardChartType.Bar;
  }
};

interface TileEditingContext {
  formValues: Record<string, unknown>;
  groupBy: string;
  chartKind: ChartKind;
  yKey: string;
  view: string;
  /** Whether the current analytics response has aggregate totals available. Required to allow saving a KPI tile. */
  totalsPresent: boolean;
  /** Columns metadata from the current response — used as the canonical metric list for tile charts. */
  responseColumns: ColumnMetaDto[];
}

export const TILE_DIALOG_CHART_TYPES = [DashboardChartType.Kpi, DashboardChartType.Pie, DashboardChartType.Bar] as const;

export const useTileEditing = (config: AnalyticsDomainConfig, context: TileEditingContext, onBackToOverview?: () => void) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const activeScope = useAppSelector(selectActiveTileScope);
  const companyId = useAppSelector(selectIdentityCompanyId);

  const [tileDialogOpen, setTileDialogOpen] = useState(false);
  const [tileDialogMode, setTileDialogMode] = useState<'create' | 'edit'>('create');
  const [tileDialogSaving, setTileDialogSaving] = useState(false);
  const [editingTileId, setEditingTileId] = useState<string | undefined>();
  const [editingTileTitle, setEditingTileTitle] = useState<string | undefined>();
  const [editingTileScope, setEditingTileScope] = useState<TileScope>('personal');
  const [editingTileChartType, setEditingTileChartType] = useState<DashboardChartType | undefined>();
  const [editingTileMetric, setEditingTileMetric] = useState<string | undefined>();

  const canSaveAsChart = context.view === 'chart' && context.groupBy !== NO_GROUP_BY;
  const canSaveAsTile = canSaveAsChart || context.totalsPresent;

  const tileDialogConfig = useMemo<TileDialogConfig>(() => {
    const groupByOpt = config.groupByOptions.find((o) => o.value === context.groupBy);
    const metricCol = context.responseColumns.find((c) => c.key === context.yKey);
    const resolveSeededChartType = (): DashboardChartType => {
      if (tileDialogMode === 'edit' && editingTileChartType) return editingTileChartType;
      if (canSaveAsChart) return chartKindToChartType(context.chartKind);
      return DashboardChartType.Kpi;
    };
    const seededChartType = resolveSeededChartType();
    const seededMetric = tileDialogMode === 'edit' && editingTileMetric ? editingTileMetric : context.yKey;
    return {
      domain: t(config.card.titleKey),
      groupBy: t(groupByOpt?.labelKey ?? 'analytics.groupBy.none'),
      chartType: seededChartType,
      metric: seededMetric,
      metricLabel: t(metricCol?.labelKey ?? context.yKey),
      totalsMetrics: config.totalsMetrics,
    };
  }, [
    t,
    config,
    context.groupBy,
    context.chartKind,
    context.yKey,
    context.responseColumns,
    tileDialogMode,
    editingTileChartType,
    editingTileMetric,
    canSaveAsChart,
  ]);

  const handleOpenCreateTileDialog = useCallback(() => {
    setTileDialogMode('create');
    setTileDialogOpen(true);
  }, []);

  const handleOpenEditTileDialog = useCallback(() => {
    setTileDialogMode('edit');
    setTileDialogOpen(true);
  }, []);

  const handleTileDialogConfirm = useCallback(
    async (title: string, scope: TileScope | undefined, overrides: { chartType: DashboardChartType; metric: string }) => {
      const isKpi = overrides.chartType === DashboardChartType.Kpi;
      const allowedMetricKeys = context.responseColumns.map((c) => c.key);
      const safeChartMetric = allowedMetricKeys.includes(overrides.metric) ? overrides.metric : (context.responseColumns[0]?.key ?? 'count');
      const req = {
        domain: config.domain,
        chartType: overrides.chartType,
        title,
        filters: config.toTileFilters(context.formValues),
        groupBy: isKpi ? null : context.groupBy,
        metric: isKpi ? overrides.metric : safeChartMetric,
        page: DashboardPage.Overview,
      };

      const targetScope = scope ?? 'personal';

      setTileDialogSaving(true);
      try {
        if (tileDialogMode === 'create') {
          switch (targetScope) {
            case 'company':
              if (!companyId) return;
              await dashboardTileService.createCompanyDefault(companyId, req);
              break;
            case 'global':
              await dashboardTileService.createGlobalDefault(req);
              break;
            case 'personal':
            default:
              await dashboardTileService.createTile(req);
              break;
          }
        } else if (editingTileId) {
          switch (editingTileScope) {
            case 'company':
              if (!companyId) return;
              await dashboardTileService.updateCompanyDefault(companyId, editingTileId, req);
              break;
            case 'global':
              await dashboardTileService.updateGlobalDefault(editingTileId, req);
              break;
            case 'personal':
            default:
              await dashboardTileService.updateTile(editingTileId, req);
              break;
          }
          setEditingTileId(undefined);
          setEditingTileTitle(undefined);
          setEditingTileScope('personal');
          setEditingTileChartType(undefined);
          setEditingTileMetric(undefined);
        }
        dispatch(refreshDashboardTiles());
        setTileDialogOpen(false);
        onBackToOverview?.();
      } catch {
        // BaseService shows error toast; keep dialog open for retry
      } finally {
        setTileDialogSaving(false);
      }
    },
    [
      config,
      context.formValues,
      context.groupBy,
      context.yKey,
      context.responseColumns,
      tileDialogMode,
      editingTileId,
      editingTileScope,
      companyId,
      dispatch,
      onBackToOverview,
    ],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!editingTileId) return;

    try {
      let deleted: boolean;
      switch (editingTileScope) {
        case 'company':
          if (!companyId) return;
          deleted = await dashboardTileService.deleteCompanyDefault(companyId, editingTileId);
          break;
        case 'global':
          deleted = await dashboardTileService.deleteGlobalDefault(editingTileId);
          break;
        case 'personal':
        default:
          deleted = await dashboardTileService.deleteTile(editingTileId);
          break;
      }

      if (!deleted) return;
      dispatch(refreshDashboardTiles());
      setEditingTileId(undefined);
      setEditingTileTitle(undefined);
      setEditingTileScope('personal');
      setEditingTileChartType(undefined);
      setEditingTileMetric(undefined);
      onBackToOverview?.();
    } catch {
      // BaseService shows error toast
    }
  }, [editingTileId, editingTileScope, companyId, dispatch, onBackToOverview]);

  const handleClearEditingContext = useCallback(() => {
    setEditingTileId(undefined);
    setEditingTileTitle(undefined);
    setEditingTileScope('personal');
    setEditingTileChartType(undefined);
    setEditingTileMetric(undefined);
  }, []);

  return {
    tileDialogOpen,
    tileDialogMode,
    tileDialogSaving,
    editingTileId,
    editingTileTitle,
    editingTileScope,
    activeScope,
    canSaveAsTile,
    setTileDialogOpen,
    setEditingTileId,
    setEditingTileTitle,
    setEditingTileScope,
    setEditingTileChartType,
    setEditingTileMetric,
    tileDialogConfig,
    handleOpenCreateTileDialog,
    handleOpenEditTileDialog,
    handleTileDialogConfirm,
    handleDeleteConfirm,
    handleClearEditingContext,
  };
};
